import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  FileText, UsersThree, PaperPlaneTilt, ChartLineUp, BellRinging, HandWaving,
  Sparkle, ChartLine, MagnifyingGlass, ClipboardText, Lock, Robot,
} from "@phosphor-icons/react";

// Role-specific onboarding scripts. Assistants and research assistants only
// see steps that reflect what they can actually do — no "3. Envoyez en un
// clic" for a research reviewer, no "6. Passez à Premium" for a paid role
// that shouldn't decide subscription. Kept as data (not JSX) so the render
// path stays a plain STEPS[i] lookup.
const ADMIN_STEPS = [
  { icon: HandWaving, title: "Bienvenue sur Questiomed",
    body: "En 7 étapes rapides, découvrez comment envoyer votre premier questionnaire à un patient. Vous pouvez passer ce tutoriel à tout moment." },
  { icon: FileText, title: "1. Vos questionnaires",
    body: "Utilisez la bibliothèque de questionnaires validés (EVA, PHQ-9, GAD-7, Oswestry, IPSS…) ou créez les vôtres depuis l'onglet Questionnaires." },
  { icon: UsersThree, title: "2. Ajoutez vos patients",
    body: "Depuis l'onglet Patients, créez une fiche avec le nom, le prénom et la date de naissance. L'email est optionnel : vous pourrez l'ajouter au moment de l'envoi." },
  { icon: PaperPlaneTilt, title: "3. Envoyez en un clic",
    body: "Envoyez un questionnaire à un patient facilement depuis l'accueil ou la fiche patient." },
  { icon: ChartLineUp, title: "4. Suivez et analysez",
    body: "L'onglet Envois affiche le statut, les relances et les résultats des questionnaires." },
  { icon: BellRinging, title: "5. Personnalisez votre application",
    body: "Dans Paramètres, gérez vos informations personnelles et celles de votre cabinet, activez les notifications emails et les relances automatiques. Donnez également accès à l'application à vos collaborateurs." },
  { icon: Sparkle, title: "6. Passez à Premium",
    body: "L'offre Premium débloque les analyses avancées : études de cohortes, comparaisons statistiques, extraction IA de vos documents cliniques et export sécurisé. Un essai gratuit de 8 jours vous permet de tout tester sans engagement." },
];

const ASSISTANT_STEPS = [
  { icon: HandWaving, title: "Bienvenue sur Questiomed",
    body: "Vous accompagnez le praticien du cabinet dans l'envoi et le suivi des questionnaires patients. Ce tutoriel vous présentera rapidement tout ce que vous pouvez faire." },
  { icon: UsersThree, title: "1. Gérez les patients du cabinet",
    body: "Créez et modifiez les fiches patients depuis l'onglet Patients." },
  { icon: PaperPlaneTilt, title: "2. Envoyez un questionnaire",
    body: "Depuis Patients ou Accueil, envoyez un questionnaire par email ou générez un lien à transmettre. Vous pouvez également remplir en consultation avec le praticien." },
  { icon: ChartLineUp, title: "3. Relancez et suivez",
    body: "L'onglet Envois liste les questionnaires en attente, complétés ou à relancer. Vous pouvez relancer un patient par email en un clic ou renvoyer plusieurs rappels à plusieurs patients d'un coup." },
  { icon: ClipboardText, title: "4. Consultez les réponses",
    body: "Toutes les réponses complétées sont dans l'onglet Réponses, avec les scores et l'export Excel." },
];

const RESEARCH_STEPS = [
  { icon: HandWaving, title: "Bienvenue sur Questiomed",
    body: "Votre rôle : exploiter les données de questionnaires complétés du cabinet pour la recherche clinique. Ce tour rapide présente vos outils." },
  { icon: ClipboardText, title: "1. Les questionnaires complétés",
    body: "L'onglet Réponses regroupe toutes les réponses patients du cabinet, avec scores, interprétations et filtres par questionnaire ou par période. Export Excel disponible." },
  { icon: ChartLine, title: "2. Analyses statistiques — Vue simple",
    body: "L'onglet Études propose une analyse par questionnaire : distribution des scores, moyennes, médianes, cohortes et évolution temporelle sur toutes les réponses complétées." },
  { icon: MagnifyingGlass, title: "3. Comparaison entre groupes",
    body: "Dans Études > Comparaison, comparez deux sous-cohortes (par âge, sexe, période, score)." },
  { icon: Robot, title: "4. Extraction IA depuis vos CR",
    body: "Dans Études > Analyse de documents, chargez un compte-rendu (PDF, Word) et l'IA extrait automatiquement les variables cliniques que vous recherchez pour intégration à votre étude." },
];

const SCRIPTS = {
  admin: ADMIN_STEPS,
  assistant: ASSISTANT_STEPS,
  research: RESEARCH_STEPS,
};

export default function OnboardingTour() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [actionDone, setActionDone] = useState({});

  const STEPS = useMemo(() => SCRIPTS[user?.role] || ADMIN_STEPS, [user?.role]);

  useEffect(() => {
    if (user && user.onboarding_completed === false) {
      setOpen(true);
      setStep(0);
    }
  }, [user]);

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/me", { onboarding_completed: true });
      setUser(data);
    } catch { /* fail silently — reopen next login */ } finally {
      setOpen(false);
      setBusy(false);
    }
  };

  const s = STEPS[Math.min(step, STEPS.length - 1)];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) complete(); }}>
      <DialogContent className="bg-white max-w-md p-0 overflow-hidden" data-testid="onboarding-modal">
        <div className="bg-[#EEF3F8] px-8 pt-8 pb-6">
          <div className="w-14 h-14 rounded-full bg-white border-2 border-[#B7C4D6] flex items-center justify-center mb-4">
            <Icon size={26} weight="duotone" className="text-[#3A8FA5]" />
          </div>
          <h2 className="font-serif text-2xl text-[#1E3A5F] leading-tight">{s.title}</h2>
        </div>
        {/* Fixed content height so the modal doesn't jump between steps. */}
        {/* min-h chosen empirically = tallest step (5 lines of body + progress + footer). */}
        <div className="px-8 py-6 space-y-6 flex flex-col min-h-[280px]">
          <p className="text-sm text-[#5F758F] leading-relaxed flex-1">{s.body}</p>
          {s.action && (
            <div className="rounded-[12px] bg-[#F4F7FB] border border-[#E1E9F0] px-4 py-3">
              {actionDone[step] ? (
                <p className="text-xs text-[#3A8FA5] font-semibold flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#3A8FA5]" /> {s.action.done}
                </p>
              ) : (
                <Button
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await s.action.run({ user });
                      setActionDone((a) => ({ ...a, [step]: true }));
                    } catch (e) {
                      /* silent — user can still advance */
                    } finally { setBusy(false); }
                  }}
                  disabled={busy}
                  className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold h-9 text-[13px]"
                  data-testid={s.action.testid}
                >
                  {busy ? "…" : s.action.label}
                </Button>
              )}
            </div>
          )}
          <div className="flex items-center gap-1.5" aria-label="Progression">
            {STEPS.map((it, i) => (
              <span
                key={it.title}
                className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[#3A8FA5]" : i < step ? "w-1.5 bg-[#3A8FA5]" : "w-1.5 bg-[#E1E9F0]"}`}
              />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={complete}
              disabled={busy}
              className="text-xs tracking-[0.14em] uppercase text-[#5F758F] hover:text-[#1E3A5F] transition-colors"
              data-testid="onboarding-skip"
            >
              Passer le tutoriel
            </button>
            <div className="flex gap-2">
              {step > 0 && (
                <Button variant="outline" onClick={() => setStep(step - 1)} disabled={busy} className="rounded-md" data-testid="onboarding-prev">
                  Précédent
                </Button>
              )}
              {isLast ? (
                <Button onClick={complete} disabled={busy} className="rounded-md bg-[#1E3A5F] hover:bg-[#0F2340] text-white" data-testid="onboarding-done">
                  {busy ? "…" : "C'est parti"}
                </Button>
              ) : (
                <Button onClick={() => setStep(step + 1)} className="rounded-md bg-[#1E3A5F] hover:bg-[#0F2340] text-white" data-testid="onboarding-next">
                  Suivant
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
